/* eslint-disable @typescript-eslint/no-explicit-any */
import { Button, Typography } from 'omakase';
import { DeleteAccountDialog } from './DeleteAccountDialog';
import { IWithContextProps, safeToNumber, withContext } from '@tock/coretypes';
import { NavLink, RouteComponentProps, withRouter } from 'react-router-dom';
import { Theme, css } from '@emotion/react';
import { find } from 'es-toolkit/compat';
import { getUpcomingBookings } from 'utils/profileUtils';
import { oc } from 'ts-optchain';
import { requiresEmailConfirmation } from '@tock/shared/utils/patron';
import AccountPageMobileHeading from 'components/account/AccountPageMobileHeading';
import Anchor from 'components/Anchor';
import BaseComponent from '@tock/shared/components/BaseComponent';
import ChangeEmailForm from 'forms/ChangeEmailForm';
import ConsumerPanel from 'components/ConsumerPanel';
import EditProfileForm from 'forms/account/EditProfileForm';
import ResyLinkedAccountBanner from 'components/account/ResyLinkedAccountBanner';
import connect, { DefaultConnectProps, StoreType } from '@tock/shared/utils/connect';
import protos from 'utils/protos';

const { PatronProfile, AuthProviders } = protos;
const UNDERAGE_PATRON = 5024;

interface ConnectProps extends DefaultConnectProps {
  patron: StoreType['patron'];
}

class AccountSettingsRoute extends BaseComponent<
  ConnectProps & RouteComponentProps & IWithContextProps
> {
  componentDidMount() {
    this.props.actions.navigation.set({
      hasLogo: true,
      hasBack: false,
    });
    this.props.actions.app.getActiveEmailChange();
    this.props.actions.app.getPatronAuthTypes();
    this.props.actions.app.fetchPatronProfile();
    this.props.actions.patron.fetchPurchaseHistory();
    this.handleLoadData();
  }

  handleLoadData() {
    this.props.actions.app.fetchRoles();
  }

  handleForgotPassword() {
    if (this.props.app.patron && this.props.app.patron.email) {
      this.props.actions.app.resetPassword(this.props.app.patron.email);
    }
  }

  handleReauthenticateAndUpdateEmail(formValues: any) {
    const { email, password } = formValues;
    this.props.actions.app
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      .login(this.props.app.patron!.email, password)
      .then(() => this.props.actions.app.changeEmail(email))
      .then(() => this.props.actions.app.getActiveEmailChange())
      .catch((response) => {
        this.props.actions.toast.showError(
          safeToNumber(response.error.errorCode) === 5001 // AUTHENTICATION_FAILED
            ? 'Invalid password'
            : response.error.message
        );
      });
  }

  isPasswordType() {
    if (this.props.app.authTypes) {
      return (
        typeof find(this.props.app.authTypes, (type) => {
          return type === AuthProviders.ProviderType.PASSWORD;
        }) !== 'undefined'
      );
    }
    return false;
  }

  updateProfile(formValues: any, newProfileImage: any, orientation: number) {
    return this.props.actions.patron
      .validatePhoneRequest({
        phone: formValues.phone,
        phoneCountryCode: formValues.phoneCountryCode,
      })
      .then((response) => {
        const { payload } = response;
        return this.updateProfileInternal(
          formValues,
          newProfileImage,
          orientation,
          payload.beautifiedPhone || formValues.phone
        );
      });
  }

  updateProfileInternal(formValues: any, newProfileImage: any, orientation: any, phone: string) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const emailUpdate = requiresEmailConfirmation(this.props.app.patron!)
      ? { email: formValues.email }
      : {};

    let day: any = null;
    if (formValues.birthdayDay || formValues.birthdayMonth || formValues.birthdayYear) {
      day = [
        {
          day: formValues.birthdayDay,
          month: formValues.birthdayMonth,
          year: formValues.birthdayYear,
          type: 'BIRTHDAY',
        },
      ];
    }

    const updatedProfile = PatronProfile.fromObject({
      ...this.props.app.patronProfile,
      patron: {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        ...this.props.app.patronProfile!.patron,
        firstName: formValues.firstName,
        isoCountryCode: formValues.isoCountryCode,
        lastName: formValues.lastName,
        phone,
        zipCode: formValues.zipCode,
        phoneCountryCode: formValues.phoneCountryCode,
        smsPhoneConsentStatus: formValues.smsPhoneConsentStatus,
        ...emailUpdate,
      },
      company: formValues.company,
      jobTitle: formValues.jobTitle,
      day,
    });
    return this.props.actions.app
      .updatePatronProfile(updatedProfile)
      .then(this.props.actions.app.fetchPatronProfile)
      .catch((error: any) => {
        this.handleProfileResponse(error);
      });
  }

  handleProfileResponse(response: any) {
    const errorCode = (oc(response) as any).error.errorCode(false)
      ? response.error.errorCode.toInt()
      : 0;
    if (errorCode === UNDERAGE_PATRON) {
      this.props.history.push('/logout');
      this.props.actions.toast.showError(
        'Tock accounts are not available for guests under 18 years of age.'
      );
      return;
    }
  }

  render() {
    const { detectedLocation, patronProfile } = this.props.app;
    const { purchaseHistory } = this.props.patron;
    const hasEmailChangeInProgress =
      this.props.app.nextEmail !== null && this.props.app.nextEmail !== '';
    const hasPasswordAuthType = this.isPasswordType();
    const disableResyManagedFields =
      this.props.context.isFeatureEnabled('JCE_BRIDGE_DISABLE_CONSUMER_ACCOUNT_EDIT') &&
      !!patronProfile?.hasGdusAccount;

    const patron = patronProfile ? patronProfile.patron : null;
    if (!patron) {
      return null;
    }

    const hasFutureBookings =
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      getUpcomingBookings(purchaseHistory, this.props.app.patron!.email).length > 0;

    return (
      <div className="Account-content">
        {disableResyManagedFields && (
          <div
            css={({ spacing }: Theme) => css`
              margin: ${spacing(2)}px ${spacing(2)}px 0;
            `}
          >
            <ResyLinkedAccountBanner />
          </div>
        )}
        <AccountPageMobileHeading
          css={({ spacing }: Theme) => css`
            margin-left: ${spacing(2)}px;
          `}
        >
          Account information
        </AccountPageMobileHeading>
        <ConsumerPanel isLoading={!patronProfile}>
          <EditProfileForm
            patron={patron}
            detectedLocation={detectedLocation}
            isLoading={this.props.app.requestInProgress}
            patronProfile={patronProfile || undefined}
            onUpdateProfile={this.updateProfile}
          />
        </ConsumerPanel>

        {!disableResyManagedFields &&
          hasPasswordAuthType &&
          this.props.app.nextEmail !== null &&
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          !requiresEmailConfirmation(this.props.app.patron!) && (
            <ConsumerPanel
              heading="Update my email"
              isLoading={!patronProfile || this.props.app.requestInProgress}
            >
              <ChangeEmailForm
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                fromEmail={patron.email!}
                onSubmit={this.handleReauthenticateAndUpdateEmail}
                hasEmailChangeInProgress={hasEmailChangeInProgress}
                toEmail={this.props.app.nextEmail}
              />
            </ConsumerPanel>
          )}

        <ConsumerPanel heading="Reset password">
          <Button variant="primary" size="large" onClick={this.handleForgotPassword}>
            Reset your password
          </Button>
        </ConsumerPanel>
        <ConsumerPanel heading="Delete account">
          {!hasFutureBookings ? (
            <DeleteAccountDialog />
          ) : (
            <Typography variant="body02">
              We are unfortunately not able to cancel your account as of right now since you have an
              upcoming reservation. You will have to wait until this reservation has been canceled
              or honored, at which point you will then be able to cancel your Tock account.
            </Typography>
          )}
        </ConsumerPanel>
        <ConsumerPanel heading="Concierge">
          <div>
            <Typography variant="body02">
              If you frequently book on behalf of others, fill out{' '}
              <Anchor href="https://www.surveymonkey.com/r/SVJGGKB">this form</Anchor> using your
              company email to join our concierge program.
            </Typography>
          </div>
        </ConsumerPanel>
        <ConsumerPanel>
          <NavLink to="/profile/logout">
            <Button variant="basic" size="large">
              Log out
            </Button>
          </NavLink>
        </ConsumerPanel>
        <ConsumerPanel modifierClassName="Account-terms">
          <Typography variant="body02">
            <Anchor className="Account-link" href="/terms">
              Tock Terms of Use
            </Anchor>
          </Typography>
        </ConsumerPanel>
      </div>
    );
  }
}

export default withRouter(
  connect<RouteComponentProps, ConnectProps>(withContext(AccountSettingsRoute), ['app', 'patron'])
);
